import {Box, HBox} from '../../../../../src/components/core/view';
import {Buyer, MostRecentAddress} from '../../../../../src/driver/types/common';
import {CheckIcon, Divider, WarningIcon} from 'native-base';
import {CustomText, H6Text} from '@buncha/components/core/text';
import React, {Fragment, useCallback, useMemo} from 'react';
import {Task, momentFormatter} from '@buncha/config/onfleet/types';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import BuyerAddressCell from '../../common/buyerAddressCell';
import BuyerDetailCell from '../../common/buyerDetailCell';
import DeliveryInstructions from './deliveryInstructions';
import DeliveryNote from './deliveryNote';
import {DeliveryNotes} from '../../../../../src/driver/types/orderDetails';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../../../../../src/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {StyleSheet} from 'react-native';
import {camelToFlat} from '@buncha/driver/shared/util';
import font from '@buncha/appStyles/style/font';
import moment from 'moment';
import {useDeliveryDetailsContext} from '@buncha/driver/hooks/deliveryDetails';
import {useGetLocale} from '@buncha/localization/hooks';

interface NameAndAddressDetailsProps {
  forOrderDetails: boolean;
  buyerInfo: Buyer | undefined;
  deliveryInstructions?: string;
  deliveryNote?: DeliveryNotes;
  buyerAddress: MostRecentAddress | undefined;
  orderId?: number;
  runId?: number;
  conversationId?: string;
  storeId?: number;
  currentTask: Task | undefined;
  deliveryDetailsPage?: boolean;
  bagsUsed?: number;
}

const keysToExclude = ['runId', 'orderId', 'userId', 'customerTag'];

const NameAndAddressDetails: React.FC<NameAndAddressDetailsProps> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const buyerAddress = props?.buyerAddress;
  const deliveryInstruction =
    props?.deliveryInstructions || buyerAddress?.deliveryInstruction;
  const dropPoint = buyerAddress?.dropPoint;
  const storeId = props?.storeId;
  const currentTask = props?.currentTask;
  const {state} = useDeliveryDetailsContext();
  const {title, body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const completeAfter = moment.unix(
    Number(currentTask?.completeAfter) ?? Date.now(),
  );
  const completeBefore = moment.unix(
    Number(currentTask?.completeBefore) ?? Date.now(),
  );

  const customerTagName = useMemo(() => {
    return Object.entries(currentTask?.metadata || {})?.find(([key, value]) => {
      if (key === 'customerTag') return value;
    })?.[1];
  }, [currentTask?.metadata]);

  const getSubDetail = useCallback(
    (header: string, subheader: string, divider?: boolean) => {
      return (
        <Fragment>
          <Box style={styles.completionTimeBox}>
            <H6Text
              style={[
                styles.textAlignment,
                {
                  color: theme?.text?.blue?.[300],
                  fontFamily: muli?.[700]?.normal,
                },
              ]}>
              {header}
            </H6Text>
            <CustomText
              style={[
                styles.textAlignment,
                {
                  color: theme?.text?.gray?.[100],
                  fontFamily: muli?.[600]?.normal,
                },
              ]}>
              {subheader}
            </CustomText>
          </Box>
          <If condition={divider}>
            <Divider />
          </If>
        </Fragment>
      );
    },
    [muli, theme?.text?.blue, theme?.text?.gray],
  );

  return (
    <Box>
      <Box style={styles.cellBox}>
        <BuyerAddressCell
          currentTask={currentTask}
          buyerAddress={props?.buyerAddress}
        />
        <Divider />
        <BuyerDetailCell
          buyerDetails={props?.buyerInfo}
          hideAddress={true}
          orderId={props.orderId}
          runId={props?.runId}
          conversationId={props?.conversationId}
          storeId={storeId}
          customerTagName={customerTagName}
          bagsUsed={props?.bagsUsed}
          currentTask={currentTask}
        />
      </Box>
      <Divider />
      <If condition={currentTask?.completeAfter && currentTask?.completeBefore}>
        {getSubDetail(
          title?.deliveryWindow,
          `${moment(completeAfter).calendar(
            moment(),
            momentFormatter,
          )}${completeAfter.format(', hh:mm A')} - ${completeBefore.format(
            'hh:mm A',
          )}`,
          true,
        )}
      </If>
      <If
        condition={
          currentTask?.destinationAddress?.notes && !props?.deliveryDetailsPage
        }>
        {getSubDetail(
          title?.destinationNotes,
          `${currentTask?.destinationAddress?.notes}`,
          true,
        )}
      </If>
      <If condition={deliveryInstruction || dropPoint}>
        <DeliveryInstructions
          deliveryInstruction={deliveryInstruction}
          dropPoint={dropPoint}
        />
        <Divider />
      </If>
      <If condition={props?.forOrderDetails}>
        <If condition={props?.deliveryNote}>
          <DeliveryNote deliveryNote={props?.deliveryNote} />
          <Divider />
        </If>
      </If>
      <If condition={currentTask?.barcodes?.length}>
        <Box style={styles.completionTimeBox}>
          <HBox>
            <H6Text
              style={[
                styles.textAlignment,
                {
                  color: theme?.text?.blue?.[300],
                  fontFamily: muli?.[700]?.normal,
                },
              ]}>
              {body?.barcodes}
            </H6Text>
            <Box
              style={[
                styles.bagsUsedContainer,
                {backgroundColor: theme?.background.yellow[100]},
              ]}>
              <CustomText
                style={[
                  {
                    color: theme?.text.blue[300],
                    ...font.FontSize.fontP10,
                    fontFamily: font.fonts.muliSemiBold,
                  },
                ]}>
                {body?.codes?.replace('{bagsUsed}', props?.bagsUsed)}
              </CustomText>
            </Box>
          </HBox>
          <HBox style={styles.barcodesContainer}>
            {currentTask?.barcodes?.map(barcode => {
              const isScanned =
                state.barcodes.findIndex(b => b.data === barcode.data) !== -1;
              return (
                <HBox key={barcode.data} style={styles.barcodeContainer}>
                  <CustomText
                    style={[
                      styles.textAlignment,
                      {
                        color: theme?.text?.gray?.[100],
                        fontFamily: muli?.[600]?.normal,
                      },
                    ]}>
                    {barcode.data}
                  </CustomText>
                  <If condition={barcode.blockCompletion}>
                    <If condition={!isScanned}>
                      <WarningIcon color={theme?.icon.red[100]} />
                    </If>
                  </If>
                  <If condition={isScanned}>
                    <CheckIcon color={theme?.icon.green[100]} />
                  </If>
                </HBox>
              );
            })}
          </HBox>
        </Box>
        <Divider />
      </If>
      <If
        condition={
          Number(currentTask?.quantity) && !props?.deliveryDetailsPage
        }>
        {getSubDetail(body?.quantity, `${currentTask?.quantity}`, true)}
      </If>
      <If
        condition={
          currentTask?.metadata && Object.values(currentTask?.metadata)?.length
        }>
        {Object.entries(currentTask?.metadata || {})?.map(([key, value]) => {
          if (keysToExclude.includes(key)) return <></>;
          return getSubDetail(camelToFlat(key), value, true);
        })}
      </If>
    </Box>
  );
};
export default React.memo(NameAndAddressDetails);

const styles = StyleSheet.create({
  cellBox: {
    marginVertical: 10,
  },
  textAlignment: {
    textAlign: 'left',
  },
  completionTimeBox: {
    marginVertical: 10,
    gap: 5,
  },
  barcodeContainer: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 10,
    width: '45%',
  },
  barcodesContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flex: 1,
    flexWrap: 'wrap',
  },
  bagsUsedContainer: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
  },
});
